/**
 * Editorial source-of-truth for Hive Homes' developer pages.
 *
 * The live listing feed carries no developer field, so each entry
 * declares `keywords` — lowercase needles matched against a listing's
 * community, sub-community, address, and title — to associate live
 * stock with the developer. Curated the same way `communities.ts` is.
 *
 * Kept free of bundler asset imports so the server (sitemap, prerender)
 * can import this module too; logo files live in
 * `src/lib/developer-logos.ts`, which only client components import.
 */

export interface DeveloperContent {
  slug: string;
  name: string;
  /** One-liner used on cards and the developer page banner. */
  blurb: string;
  seoTitle: string;
  seoDescription: string;
  /** Editorial paragraphs (2) for the developer page. */
  paragraphs: string[];
  /** Brand photo used for the card + page banner (public URL). */
  cover: string;
  coverAlt: string;
  /** Lowercase needles matched against listing location/title text. */
  keywords: string[];
  /** Editorial community slugs to cross-link from the developer page. */
  communitySlugs: string[];
  ctaLabel: string;
}

export const DEVELOPERS: DeveloperContent[] = [
  {
    slug: "emaar",
    name: "Emaar",
    blurb:
      "Dubai's flagship master developer — Downtown Dubai, Dubai Creek Harbour, Dubai Hills Estate and the Burj Khalifa itself.",
    seoTitle: "Emaar Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Emaar properties for sale and off-plan in Dubai — Dubai Creek Harbour, Downtown, Emaar Beachfront and more. Speak to a Hive Homes Emaar specialist.",
    paragraphs: [
      "Emaar is the developer behind Dubai's most recognisable addresses — the Burj Khalifa, Downtown Dubai, Dubai Mall, Dubai Creek Harbour, Dubai Hills Estate, and Emaar Beachfront. Its delivery track record across three decades is unmatched in the Emirate, and Emaar communities consistently hold the strongest resale and rental demand in the city.",
      "For buyers who want the security of an established master developer with genuine build quality and liquid secondary markets, Emaar is the benchmark. Our team tracks every Emaar launch and keeps current stock across its ready communities.",
    ],
    cover: "/images/communities/dubai-creek-harbour-the-lagoons.jpg",
    coverAlt: "Dubai Creek Harbour towers by Emaar at dusk",
    keywords: [
      "emaar",
      "dubai creek harbour",
      "creek harbour",
      "downtown dubai",
      "dubai hills",
      "emaar beachfront",
      "arabian ranches",
      "the valley",
      "rashid yachts",
      "mina rashid",
    ],
    communitySlugs: ["dubai-creek-harbour-the-lagoons", "dubai-harbour", "zabeel"],
    ctaLabel: "Speak to an Emaar Specialist",
  },
  {
    slug: "damac",
    name: "DAMAC",
    blurb:
      "One of Dubai's most prolific private developers — Damac Lagoons, Damac Hills and branded residences across the city.",
    seoTitle: "DAMAC Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse DAMAC properties in Dubai — Damac Lagoons villas and townhouses, Damac Hills, and branded towers. Off-plan and ready stock with Hive Homes.",
    paragraphs: [
      "DAMAC Properties is one of Dubai's largest private developers, with a portfolio spanning master-planned villa communities like Damac Lagoons and Damac Hills to branded high-rises with Cavalli, de GRISOGONO, and Versace. Its launches move quickly and its payment plans are consistently among the most buyer-friendly in the market.",
      "We hold current availability across DAMAC's lagoon clusters and hills communities, including secondary-market resales from original buyers — often at compelling prices against the developer's current launch phases.",
    ],
    cover: "/images/communities/damac-lagoons.jpg",
    coverAlt: "Aerial view of the Damac Lagoons crystal lagoon clusters",
    keywords: ["damac", "damac lagoons", "damac hills"],
    communitySlugs: ["damac-lagoons", "damac-hills-2"],
    ctaLabel: "Speak to a DAMAC Specialist",
  },
  {
    slug: "sobha",
    name: "Sobha",
    blurb:
      "Premium build quality and full backward integration — Sobha Hartland and waterfront living in MBR City.",
    seoTitle: "Sobha Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Sobha Realty properties in Dubai — Sobha Hartland apartments and villas in MBR City. Premium finishes, waterfront living. Hive Homes Dubai.",
    paragraphs: [
      "Sobha Realty is known for one thing above all: build quality. As one of the few fully backward-integrated developers in the region — with its own design, engineering, and construction arms — Sobha controls every stage of delivery. Sobha Hartland in Mohammed Bin Rashid City has become one of Dubai's most desirable premium communities.",
      "Sobha stock is consistently in demand from end-users who value finish quality over headline price. We track availability across Hartland's apartments, townhouses, and villas, on both the primary and secondary market.",
    ],
    cover: "/images/communities/meydan.jpg",
    coverAlt: "Meydan and MBR City skyline, home of Sobha Hartland",
    keywords: ["sobha", "hartland"],
    communitySlugs: ["meydan", "mohammed-bin-rashid-city"],
    ctaLabel: "Speak to a Sobha Specialist",
  },
  {
    slug: "ellington",
    name: "Ellington",
    blurb:
      "Design-led boutique developer — considered architecture and interiors across JVC, Downtown and Palm Jumeirah.",
    seoTitle: "Ellington Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Ellington Properties in Dubai — design-led apartments in JVC, Business Bay, Downtown and Palm Jumeirah. Off-plan and ready with Hive Homes.",
    paragraphs: [
      "Ellington Properties is Dubai's leading design-led boutique developer. Rather than chasing scale, Ellington focuses on considered architecture, genuinely liveable layouts, and interiors that photograph the way they feel. Its buildings in Jumeirah Village Circle, Business Bay, and Downtown routinely outperform their neighbourhoods on both rental yield and resale premium.",
      "We work closely with Ellington launches and hold ready stock across its completed buildings — a natural fit for buyers who want boutique quality without the master-developer premium.",
    ],
    cover: "/images/communities/jumeirah-village-circle.jpg",
    coverAlt: "Jumeirah Village Circle skyline, home to Ellington's residential buildings",
    keywords: ["ellington", "belgravia"],
    communitySlugs: ["jumeirah-village-circle", "business-bay"],
    ctaLabel: "Speak to an Ellington Specialist",
  },
  {
    slug: "dubai-holding",
    name: "Dubai Holding",
    blurb:
      "The Emirate's sovereign developer — Madinat Jumeirah Living, Jumeirah assets and city-scale destinations.",
    seoTitle: "Dubai Holding Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Dubai Holding residential properties — Madinat Jumeirah Living and destination communities across Dubai. Speak to a Hive Homes specialist.",
    paragraphs: [
      "Dubai Holding is the Emirate's sovereign-backed developer, with a portfolio that spans Madinat Jumeirah Living, Jumeirah's hospitality estate, and some of the city's most ambitious destination projects. Its residential communities carry a rare combination of location quality and long-term institutional backing.",
      "Madinat Jumeirah Living in particular — freehold residences beside the Burj Al Arab — has become one of Dubai's strongest-performing premium addresses. We track availability across Dubai Holding's residential portfolio for both end-users and investors.",
    ],
    cover: "/images/communities/umm-suqeim.jpg",
    coverAlt: "Umm Suqeim and the Burj Al Arab, beside Madinat Jumeirah Living",
    keywords: ["dubai holding", "madinat jumeirah", "jumeirah living", "mjl"],
    communitySlugs: ["umm-suqeim"],
    ctaLabel: "Speak to a Dubai Holding Specialist",
  },
  {
    slug: "meraas",
    name: "Meraas",
    blurb:
      "Lifestyle destinations by design — City Walk, Bluewaters, La Mer and Port de La Mer.",
    seoTitle: "Meraas Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Meraas properties in Dubai — City Walk, Bluewaters, Port de La Mer and La Mer residences. Ready and off-plan stock with Hive Homes.",
    paragraphs: [
      "Meraas builds the places Dubai actually spends its weekends — City Walk, Bluewaters, La Mer, and Port de La Mer. Its residential communities sit inside genuine lifestyle destinations, which translates into resilient rental demand and some of the strongest walk-to-everything living in the city.",
      "Meraas residences suit buyers who want urban, design-forward living over suburban villa formats. We hold current availability across City Walk and the La Mer coastline, with early access to new phase releases.",
    ],
    cover: "/images/communities/city-walk.jpg",
    coverAlt: "City Walk by Meraas — urban residential streets in central Dubai",
    keywords: ["meraas", "city walk", "bluewaters", "port de la mer", "la mer", "central park"],
    communitySlugs: ["city-walk"],
    ctaLabel: "Speak to a Meraas Specialist",
  },
  {
    slug: "nakheel",
    name: "Nakheel",
    blurb:
      "The master developer behind Palm Jumeirah, Jumeirah Islands, Al Furjan, Dubai Islands and Palm Jebel Ali — Dubai's coastline, reimagined.",
    seoTitle: "Nakheel Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Nakheel properties for sale and off-plan in Dubai — Palm Jumeirah, Palm Jebel Ali, Dubai Islands, Jumeirah Islands and Al Furjan. Speak to a Hive Homes Nakheel specialist.",
    paragraphs: [
      "Nakheel is the government-backed master developer that gave Dubai its coastline — Palm Jumeirah, Jumeirah Islands, Jumeirah Park, The Gardens and Al Furjan, with Palm Jebel Ali and Dubai Islands now underway. Few developers anywhere have shaped a city's geography so literally, and Nakheel's waterfront communities remain among the most sought-after addresses in the Emirate.",
      "Nakheel stock ranges from established villa communities with mature landscaping to new-phase launches on Palm Jebel Ali and Dubai Islands. We track both — the resale market across its completed communities and early releases on its next generation of waterfront plots.",
    ],
    cover: "/images/developers/nakheel.jpg",
    coverAlt: "Aerial view of Palm Jumeirah's fronds and Atlantis, by Nakheel",
    keywords: [
      "nakheel",
      "palm jumeirah",
      "palm jebel ali",
      "dubai islands",
      "jumeirah islands",
      "jumeirah park",
      "al furjan",
      "discovery gardens",
      "the gardens",
      "nad al sheba",
      "jebel ali village",
      "como residences",
      "palm beach towers",
    ],
    communitySlugs: ["jumeirah-village-circle", "mohammed-bin-rashid-city"],
    ctaLabel: "Speak to a Nakheel Specialist",
  },
  {
    slug: "binghatti",
    name: "Binghatti",
    blurb:
      "Dubai's fastest-moving high-rise developer — Bugatti Residences, Mercedes-Benz Places and the signature Binghatti façade across Business Bay and JVC.",
    seoTitle: "Binghatti Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Binghatti properties in Dubai — Business Bay, JVC and Downtown towers including Bugatti Residences and Mercedes-Benz Places. Off-plan and ready with Hive Homes.",
    paragraphs: [
      "Binghatti has become one of the most visible names on Dubai's skyline, delivering towers at a pace few private developers can match. Its interlocking balcony façades are instantly recognisable across Business Bay and Jumeirah Village Circle, and its branded collaborations — Bugatti Residences, Mercedes-Benz Places and the Jacob & Co-branded Burj Binghatti — sit at the very top of the branded-residence market.",
      "Binghatti's speed of delivery makes it a favourite with investors who want short hold periods between launch and handover. We follow every release and hold ready stock across its completed buildings in Business Bay and JVC.",
    ],
    cover: "/images/developers/binghatti.jpg",
    coverAlt: "Business Bay towers along the Dubai Canal at sunset",
    keywords: ["binghatti", "bugatti residences", "mercedes-benz places"],
    communitySlugs: ["business-bay", "jumeirah-village-circle", "downtown-dubai"],
    ctaLabel: "Speak to a Binghatti Specialist",
  },
  {
    slug: "azizi",
    name: "Azizi",
    blurb:
      "One of Dubai's largest private developers — Azizi Riviera on the Meydan canal and a city-wide pipeline of well-priced apartments.",
    seoTitle: "Azizi Developments Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Azizi Developments properties in Dubai — Azizi Riviera in Meydan, Al Furjan, Dubai South and more. Ready and off-plan apartments with Hive Homes.",
    paragraphs: [
      "Azizi Developments is one of the largest private developers in Dubai by unit count, with a pipeline that stretches from Al Furjan and Dubai Sports City to the canal-side Azizi Riviera in Meydan and the lagoon community of Azizi Venice in Dubai South. Its focus is accessible, well-located apartments delivered at scale.",
      "For investors, Azizi offers some of the lowest entry prices in freehold Dubai without leaving established transport corridors. We track handovers across Riviera and hold current availability on both the primary and secondary market.",
    ],
    cover: "/images/developers/azizi.jpg",
    coverAlt: "Dubai skyline reflected in the canal at night, near Azizi Riviera",
    keywords: ["azizi", "riviera"],
    communitySlugs: ["mohammed-bin-rashid-city"],
    ctaLabel: "Speak to an Azizi Specialist",
  },
  {
    slug: "danube",
    name: "Danube",
    blurb:
      "Dubai's 1% payment-plan pioneer — fully furnished, amenity-rich apartments across JVC, Business Bay, JLT and Arjan.",
    seoTitle: "Danube Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Danube Properties in Dubai — Bayz, Elitz, Diamondz, Oceanz and more with 1% monthly payment plans. Off-plan and ready stock with Hive Homes.",
    paragraphs: [
      "Danube Properties made its name with the 1% monthly payment plan and has since become one of Dubai's most consistent mid-market developers. Its apartments come fully furnished with generous amenity decks — from Bayz in Business Bay and Diamondz in JLT to Elitz in JVC and Oceanz in Dubai Maritime City.",
      "Danube suits first-time investors and end-users who want a low upfront commitment and a turnkey finish. We keep current availability across its launches and completed buildings, including resales from original buyers.",
    ],
    cover: "/images/developers/danube.jpg",
    coverAlt: "Dubai skyline silhouetted against a golden sunset",
    keywords: [
      "danube",
      "bayz",
      "elitz",
      "gemz",
      "oceanz",
      "diamondz",
      "sportz",
      "viewz",
      "fashionz",
      "skyz",
      "opalz",
      "petalz",
      "pearlz",
      "eleganz",
      "olivz",
      "miraclz",
      "resortz",
    ],
    communitySlugs: ["jumeirah-village-circle", "business-bay", "arjan"],
    ctaLabel: "Speak to a Danube Specialist",
  },
  {
    slug: "aldar",
    name: "Aldar",
    blurb:
      "Abu Dhabi's largest listed developer, now in Dubai — Haven and Athlon in Dubailand, plus Yas Island, Saadiyat and Al Reem.",
    seoTitle: "Aldar Properties for Sale in Dubai & Abu Dhabi | Hive Homes",
    seoDescription:
      "Browse Aldar Properties in Dubai and Abu Dhabi — Haven by Aldar, Athlon, Yas Island, Saadiyat Island and Al Reem Island. Speak to a Hive Homes Aldar specialist.",
    paragraphs: [
      "Aldar Properties is the largest listed developer in Abu Dhabi and one of the most institutionally backed names in the region. Its master communities on Yas Island, Saadiyat Island and Al Reem Island set the standard for the capital, and its entry into Dubai — the wellness-led Haven villa community and the active-living Athlon, both in Dubailand — has been met with exceptional demand.",
      "Aldar's scale and governance give buyers a level of security comparable to Dubai's government-backed developers. We track its Dubai launches closely and can advise on Abu Dhabi stock for buyers looking across both Emirates.",
    ],
    cover: "/images/developers/aldar.jpg",
    coverAlt: "Sunset over Abu Dhabi's waterfront and skyline",
    keywords: [
      "aldar",
      "haven by aldar",
      "athlon",
      "yas island",
      "yas bay",
      "yas acres",
      "saadiyat",
      "al reem",
      "al raha",
      "mamsha",
      "gardenia bay",
    ],
    communitySlugs: ["dubai-land"],
    ctaLabel: "Speak to an Aldar Specialist",
  },
  {
    slug: "omniyat",
    name: "Omniyat",
    blurb:
      "Ultra-luxury by design — The Opus by Zaha Hadid, One at Palm Jumeirah and Dorchester Collection residences.",
    seoTitle: "Omniyat Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Omniyat ultra-luxury properties in Dubai — The Opus, One at Palm Jumeirah, ORLA, The Lana and Vela. Speak to a Hive Homes Omniyat specialist.",
    paragraphs: [
      "Omniyat sits at the very top of Dubai's residential market. Its buildings are commissioned from world-renowned architects — Zaha Hadid's The Opus, Foster + Partners' ORLA on the Palm — and operated with hotel partners such as Dorchester Collection at The Lana, Vela and One at Palm Jumeirah. Unit counts are deliberately small and finishes are uncompromising.",
      "Omniyat is for buyers seeking a genuine trophy asset rather than yield alone. We work discreetly with Omniyat stock and can arrange private viewings across its completed residences and forthcoming releases.",
    ],
    cover: "/images/developers/omniyat.jpg",
    coverAlt: "Burj Khalifa and the Business Bay towers above a multi-level interchange at dusk",
    keywords: [
      "omniyat",
      "the opus",
      "one at palm",
      "one palm",
      "the sterling",
      "the lana",
      "orla",
      "ava at palm",
      "vela",
      "the pad",
      "langham residences",
    ],
    communitySlugs: ["business-bay", "downtown-dubai"],
    ctaLabel: "Speak to an Omniyat Specialist",
  },
  {
    slug: "select-group",
    name: "Select Group",
    blurb:
      "Waterfront specialists — Marina Gate, Six Senses Residences on the Palm and the Peninsula district in Business Bay.",
    seoTitle: "Select Group Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Select Group properties in Dubai — Marina Gate, Six Senses Residences The Palm and Peninsula Business Bay. Ready and off-plan with Hive Homes.",
    paragraphs: [
      "Select Group is one of Dubai's most established private developers, with more than two decades of delivery focused on the waterfront. Its Marina Gate trio anchors the Dubai Marina skyline, Peninsula is transforming the Business Bay canal front, and Six Senses Residences The Palm brings a wellness-branded address to Palm Jumeirah.",
      "Select Group buildings are known for solid construction and strong tenant demand, particularly in the Marina. We hold current stock across Marina Gate and Peninsula and follow each new phase release.",
    ],
    cover: "/images/developers/select-group.jpg",
    coverAlt: "Aerial view of Dubai Marina's yacht basin surrounded by towers",
    keywords: [
      "select group",
      "marina gate",
      "peninsula",
      "six senses residences",
      "studio one",
      "the torch",
      "botanica",
      "west avenue",
    ],
    communitySlugs: ["business-bay", "dubai-harbour"],
    ctaLabel: "Speak to a Select Group Specialist",
  },
  {
    slug: "deyaar",
    name: "Deyaar",
    blurb:
      "A publicly listed developer with two decades of delivery — Regalia in Business Bay, Mar Casa on the waterfront and Midtown in DIP.",
    seoTitle: "Deyaar Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Deyaar Development properties in Dubai — Regalia Business Bay, Mar Casa, Tria, Midtown and Amalia. Ready and off-plan with Hive Homes.",
    paragraphs: [
      "Deyaar Development is one of Dubai's longest-standing listed developers, with a portfolio spanning Business Bay's Regalia, the sea-facing Mar Casa in Dubai Maritime City, Tria in Silicon Oasis and the Midtown community in Dubai Investment Park. Its balance sheet and DFM listing give buyers a level of transparency that few private developers match.",
      "Deyaar's buildings offer reliable build quality at mid-market prices and typically hand over on schedule. We hold current availability across its completed towers and track each new launch.",
    ],
    cover: "/images/developers/deyaar.jpg",
    coverAlt: "Dubai's illuminated skyline and highways at night from above",
    keywords: [
      "deyaar",
      "regalia",
      "mar casa",
      "the atria",
      "millennium atria",
      "tria by deyaar",
      "midtown",
      "bella rose",
      "eleve",
      "rosalia",
      "amalia",
      "talia",
    ],
    communitySlugs: ["business-bay"],
    ctaLabel: "Speak to a Deyaar Specialist",
  },
  {
    slug: "mag",
    name: "MAG",
    blurb:
      "Wellness-led and design-focused — MAG Eye and MAG City in Meydan, Keturah on Dubai Creek and towers in Business Bay.",
    seoTitle: "MAG Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse MAG properties in Dubai — MAG Eye, MAG City, Keturah Resort and Business Bay towers. Off-plan and ready stock with Hive Homes.",
    paragraphs: [
      "MAG is the property arm of MAG Group, one of the UAE's largest privately owned conglomerates, and has built a residential portfolio centred on Meydan and MBR City — MAG Eye's townhouses and apartments, MAG City, and the wellness-branded Keturah Resort on Dubai Creek. Its Business Bay towers round out a city-wide footprint.",
      "MAG appeals to buyers who value considered master-planning and green space close to Downtown. We track availability across its Meydan communities and hold resale stock from completed phases.",
    ],
    cover: "/images/developers/mag.jpg",
    coverAlt: "Aerial view of Downtown Dubai's towers on a clear day",
    keywords: [
      "mag eye",
      "mag city",
      "mag 5",
      "mag 318",
      "mag 330",
      "mag 218",
      "mag 22",
      "keturah",
      "mag lifestyle",
      "mag property",
    ],
    communitySlugs: ["mohammed-bin-rashid-city", "business-bay"],
    ctaLabel: "Speak to a MAG Specialist",
  },
  {
    slug: "majid-al-futtaim",
    name: "Majid Al Futtaim",
    blurb:
      "The retail giant behind Mall of the Emirates, now master developer of Tilal Al Ghaf — lagoon villas and Ghaf Woods forest living.",
    seoTitle: "Majid Al Futtaim Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Majid Al Futtaim properties in Dubai — Tilal Al Ghaf villas and townhouses around Lagoon Al Ghaf, and Ghaf Woods. Speak to a Hive Homes specialist.",
    paragraphs: [
      "Majid Al Futtaim is best known for Mall of the Emirates, City Centre and Carrefour, but its communities arm has quietly built one of Dubai's most admired master developments: Tilal Al Ghaf, a low-density villa and townhouse community wrapped around the swimmable Lagoon Al Ghaf. Its next chapter, Ghaf Woods, brings forest-first living to Dubailand.",
      "Tilal Al Ghaf's completed phases — Harmony, Elan, Aura and Alaya — command a consistent premium on the secondary market. We hold current availability across the community and follow every new release.",
    ],
    cover: "/images/communities/tilal-al-ghaf.jpg",
    coverAlt: "Tilal Al Ghaf villas along Lagoon Al Ghaf, by Majid Al Futtaim",
    keywords: [
      "majid al futtaim",
      "tilal al ghaf",
      "ghaf woods",
      "elan townhouses",
      "aura gardens",
      "aura townhouses",
      "harmony villas",
      "alaya",
      "amara",
      "serenity mansions",
      "elysian mansions",
      "lanai island",
    ],
    communitySlugs: ["tilal-al-ghaf"],
    ctaLabel: "Speak to a Tilal Al Ghaf Specialist",
  },
  {
    slug: "wasl",
    name: "wasl",
    blurb:
      "Dubai Government's real estate arm — wasl1 in Zabeel, wasl Gate on Sheikh Zayed Road and one of the city's largest rental portfolios.",
    seoTitle: "wasl Properties for Sale & Rent in Dubai | Hive Homes",
    seoDescription:
      "Browse wasl properties in Dubai — wasl1 Zabeel, wasl Gate, wasl Port Views and rentals across Deira and Bur Dubai. Speak to a Hive Homes specialist.",
    paragraphs: [
      "wasl Asset Management Group is the real estate arm of Dubai Real Estate Corporation, owning and managing one of the largest residential portfolios in the city. Its freehold developments — wasl1 beside Zabeel Park, wasl Gate on Sheikh Zayed Road and wasl Port Views in Mina Rashid — bring government-backed quality to some of Dubai's most central locations.",
      "wasl suits end-users and investors who prioritise location and long-term stewardship over headline launches. We hold current availability across its freehold buildings and can source rentals from its wider portfolio.",
    ],
    cover: "/images/developers/wasl.jpg",
    coverAlt: "The Dubai Frame in Zabeel Park, beside wasl1",
    keywords: [
      "wasl1",
      "wasl 1",
      "wasl gate",
      "wasl port views",
      "wasl village",
      "wasl crystal",
      "wasl properties",
      "wasl asset",
      "park gate residences",
      "the nook",
      "hillside residences",
    ],
    communitySlugs: ["zabeel"],
    ctaLabel: "Speak to a wasl Specialist",
  },
  {
    slug: "nshama",
    name: "Nshama",
    blurb:
      "The developer of Town Square Dubai — family townhouses and apartments around a landmark central park on Al Qudra Road.",
    seoTitle: "Nshama Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Nshama properties in Town Square Dubai — Hayat, Zahra, Safi and Rawda townhouses and apartments. Ready and off-plan with Hive Homes.",
    paragraphs: [
      "Nshama built Town Square Dubai from the ground up — a 750-acre community on Al Qudra Road anchored by a landmark central park, with more than a dozen townhouse and apartment districts including Hayat, Zahra, Safi, Rawda and Noor. It has become one of the most affordable entry points into freehold family living in Dubai.",
      "Town Square's completed phases trade actively on the secondary market, and Nshama continues to release new apartment buildings. We hold current availability across both, from three-bedroom townhouses to studio apartments.",
    ],
    cover: "/images/communities/town-square.jpg",
    coverAlt: "Town Square Dubai townhouses and central park, by Nshama",
    keywords: [
      "nshama",
      "town square",
      "townsquare",
      "hayat townhouses",
      "hayat boulevard",
      "zahra townhouses",
      "zahra apartments",
      "zahra breeze",
      "safi townhouses",
      "safi apartments",
      "rawda apartments",
      "warda apartments",
      "jenna",
      "una apartments",
      "maha townhouses",
      "reem townhouses",
      "sama townhouses",
      "naseem townhouses",
      "noor townhouses",
    ],
    communitySlugs: ["dubai-land"],
    ctaLabel: "Speak to a Town Square Specialist",
  },
  {
    slug: "al-habtoor",
    name: "Al Habtoor",
    blurb:
      "One of the UAE's oldest family conglomerates — Al Habtoor City's three residential towers on the Dubai Water Canal and Al Habtoor Tower.",
    seoTitle: "Al Habtoor Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Al Habtoor Group properties in Dubai — Al Habtoor City residences on the Dubai Water Canal, Al Habtoor Tower and more. Hive Homes Dubai.",
    paragraphs: [
      "Al Habtoor Group has been part of Dubai's story since 1970. Its real estate arm is best known for Al Habtoor City — the Noora, Amna and Meera towers on the Dubai Water Canal, sitting alongside three luxury hotels and the La Perle theatre — and for Al Habtoor Tower, one of the largest residential towers in the region by unit count.",
      "Al Habtoor City's canal-front apartments offer some of the most central hotel-serviced living in Dubai. We hold current sale and rental stock across its three towers and track releases in Al Habtoor Tower.",
    ],
    cover: "/images/developers/al-habtoor.jpg",
    coverAlt: "Illuminated bridge over the Dubai Water Canal at night, beside Al Habtoor City",
    keywords: ["habtoor", "noora tower", "amna tower", "meera tower"],
    communitySlugs: ["business-bay"],
    ctaLabel: "Speak to an Al Habtoor Specialist",
  },
  {
    slug: "arada",
    name: "Arada",
    blurb:
      "Sharjah's largest developer, now on the Dubai waterfront — Aljada and Masaar in Sharjah, Armani Beach Residences and W Residences in Dubai.",
    seoTitle: "Arada Properties for Sale in Dubai & Sharjah | Hive Homes",
    seoDescription:
      "Browse Arada properties — Armani Beach Residences Palm Jumeirah, W Residences Dubai Harbour, Aljada and Masaar in Sharjah. Speak to a Hive Homes specialist.",
    paragraphs: [
      "Arada is the developer that reshaped Sharjah's residential market with Aljada and the forested Masaar community, and it has brought that ambition to Dubai with Armani Beach Residences on Palm Jumeirah and W Residences at Dubai Harbour. Co-founded by Sharjah's Basma Group and Saudi Arabia's KBW Investments, it has delivered at a pace few young developers can match.",
      "Arada's Dubai launches sit firmly in the branded ultra-luxury segment, while its Sharjah communities offer some of the best-value family homes in the UAE. We can advise across both, and track releases at Armani Beach Residences and W Residences.",
    ],
    cover: "/images/developers/arada.jpg",
    coverAlt: "Sharjah's skyline across the water on a clear day",
    keywords: [
      "arada",
      "aljada",
      "masaar",
      "nasma residences",
      "jouri hills",
      "armani beach",
      "w residences",
    ],
    communitySlugs: ["dubai-harbour"],
    ctaLabel: "Speak to an Arada Specialist",
  },
  {
    slug: "samana",
    name: "Samana",
    blurb:
      "Private-pool apartments at accessible prices — one of Dubai's most active off-plan developers across Arjan, JVC and Dubailand.",
    seoTitle: "Samana Developers Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Samana Developers properties in Dubai — private-pool apartments in Arjan, JVC, Dubai Sports City and Dubailand with flexible payment plans. Hive Homes.",
    paragraphs: [
      "Samana Developers has grown into one of Dubai's most prolific off-plan developers by making one idea its signature: private pools on the balconies of mid-market apartments. Its buildings cluster in Arjan, Jumeirah Village Circle, Dubai Sports City and the wider Dubailand corridor, typically with long post-handover payment plans.",
      "Samana suits investors targeting short-stay and holiday-let yields, where the private-pool format commands a clear premium. We follow every launch and hold stock across its completed towers.",
    ],
    cover: "/images/communities/arjan.jpg",
    coverAlt: "Arjan, Dubai — home to several Samana residential towers",
    keywords: ["samana"],
    communitySlugs: ["arjan", "jumeirah-village-circle", "dubai-land"],
    ctaLabel: "Speak to a Samana Specialist",
  },
  {
    slug: "tiger",
    name: "Tiger",
    blurb:
      "A vertically integrated group with its own contracting arm — high-rise towers across JVC, JVT, Business Bay and Sharjah.",
    seoTitle: "Tiger Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Tiger Properties in Dubai — Neva Residences, Volga Tower, Tiger Sky Tower and more across JVC, JVT and Business Bay. Hive Homes Dubai.",
    paragraphs: [
      "Tiger Group has been building in the UAE since the 1970s, and its contracting arm gives Tiger Properties direct control over construction — a structure that keeps handovers on schedule. Its Dubai portfolio spans Jumeirah Village Circle and Triangle, Dubai Sports City and Business Bay, where Tiger Sky Tower is set to be among the tallest residential towers in the city.",
      "Tiger's competitive pricing and steady delivery make it a solid choice for investors building a rental portfolio. We hold current stock across its completed towers and track each new release.",
    ],
    cover: "/images/developers/tiger.jpg",
    coverAlt: "Aerial view of Jumeirah Village Circle's towers and curving streets",
    keywords: ["tiger", "neva residences", "volga tower", "o2 tower", "aurora tower"],
    communitySlugs: ["jumeirah-village-circle", "business-bay"],
    ctaLabel: "Speak to a Tiger Specialist",
  },
  {
    slug: "imtiaz",
    name: "Imtiaz",
    blurb:
      "Boutique towers with hotel-grade interiors — Westwood Grande in JVC, Beach Walk on Dubai Islands and Cove Edition in Dubailand.",
    seoTitle: "Imtiaz Developments Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Imtiaz Developments properties in Dubai — Westwood Grande, Beach Walk, Cove Edition, Sunset Bay and Pearl House. Off-plan and ready with Hive Homes.",
    paragraphs: [
      "Imtiaz Developments has built a reputation for fully furnished, design-led apartments delivered on time. Its Westwood Grande buildings in Jumeirah Village Circle set the template — hotel-grade finishes and smart-home fit-outs — now carried through to Beach Walk and Sunset Bay on Dubai Islands and Cove Edition in Dubailand.",
      "Imtiaz is a strong fit for investors who want a turnkey, furnished product with clear holiday-let appeal. We track each release and hold ready stock in its completed JVC towers.",
    ],
    cover: "/images/developers/imtiaz.jpg",
    coverAlt: "Dubai's coastal skyline seen across the water",
    keywords: ["imtiaz", "westwood grande", "westwood by imtiaz", "beach walk", "cove edition", "sunset bay", "pearl house"],
    communitySlugs: ["jumeirah-village-circle", "dubai-land"],
    ctaLabel: "Speak to an Imtiaz Specialist",
  },
  {
    slug: "union-properties",
    name: "Union Properties",
    blurb:
      "The developer of Motor City and Green Community — a DFM-listed pioneer with more than three decades of delivery in Dubai.",
    seoTitle: "Union Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Union Properties developments in Dubai — Motor City, Uptown Motor City, Green Community and Takaya. Ready and off-plan with Hive Homes.",
    paragraphs: [
      "Union Properties is one of Dubai's original developers, listed on the DFM since 1993 and responsible for two of the city's most liveable master communities: Motor City, built around the Dubai Autodrome, and Green Community in Dubai Investment Park. Both are known for mature landscaping, low density and consistent family demand.",
      "With Takaya now adding new apartments and townhouses to Motor City, Union Properties stock spans ready villas with established gardens through to fresh off-plan phases. We hold current availability across both communities.",
    ],
    cover: "/images/developers/union-properties.jpg",
    coverAlt: "Race cars on the Dubai Autodrome circuit in Motor City",
    keywords: [
      "union properties",
      "motor city",
      "motorcity",
      "green community",
      "uptown mirdif",
      "takaya",
      "the ribbon",
      "index tower",
      "limestone house",
    ],
    communitySlugs: ["dubai-land"],
    ctaLabel: "Speak to a Motor City Specialist",
  },
  {
    slug: "reportage",
    name: "Reportage",
    blurb:
      "Value-focused townhouses and apartments across Dubailand — Rukan, Reportage Village, Verdana and Bianca.",
    seoTitle: "Reportage Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Reportage Properties in Dubai — Rukan, Reportage Village, Verdana, Bianca and Taormina Village townhouses in Dubailand. Hive Homes Dubai.",
    paragraphs: [
      "Reportage Properties has become one of the most active developers in Dubailand and Dubai Investment Park, delivering townhouse communities like Reportage Village, Bianca, Taormina Village and Verdana at some of the most competitive price points in freehold Dubai. Its Rukan Lofts and Rukan Tower add apartments to the mix.",
      "Reportage suits buyers who want a townhouse at an apartment price and are comfortable with an emerging location. We follow every launch and hold resales from completed phases.",
    ],
    cover: "/images/developers/reportage.jpg",
    coverAlt: "Aerial view of a Dubai villa neighbourhood with tree-lined streets",
    keywords: [
      "reportage",
      "rukan lofts",
      "rukan tower",
      "verdana",
      "bianca",
      "taormina",
      "alexis tower",
      "montenapoleone",
    ],
    communitySlugs: ["dubai-land"],
    ctaLabel: "Speak to a Reportage Specialist",
  },
  {
    slug: "expo-city",
    name: "Expo City Dubai",
    blurb:
      "The legacy of Expo 2020 as a 15-minute city — Mangrove, Sky and Sidr Residences and Shamsa Townhouses beside Al Wasl Plaza.",
    seoTitle: "Expo City Dubai Properties for Sale | Hive Homes",
    seoDescription:
      "Browse Expo City Dubai properties — Mangrove Residences, Sky Residences, Sidr Residences, Shamsa Townhouses and Expo Valley villas. Speak to a Hive Homes specialist.",
    paragraphs: [
      "Expo City Dubai is the government-owned developer carrying forward the Expo 2020 site as a car-light, 15-minute city in Dubai South. Its residential launches — Mangrove Residences, Sky Residences and Sidr Residences beside Al Wasl Plaza, Shamsa Townhouses and the Expo Valley villa community — sit within one of the most sustainably designed districts in the region.",
      "With Al Maktoum International Airport's expansion and the Route 2020 Metro line on its doorstep, Expo City is one of Dubai's clearest long-term growth stories. We track each release and hold current availability.",
    ],
    cover: "/images/communities/expo-city.jpg",
    coverAlt: "Expo City Dubai, the legacy district of Expo 2020",
    keywords: [
      "expo city",
      "expo village",
      "expo valley",
      "mangrove residences",
      "sidr residences",
      "shamsa townhouses",
      "expo central",
    ],
    communitySlugs: [],
    ctaLabel: "Speak to an Expo City Specialist",
  },
  {
    slug: "object-1",
    name: "Object 1",
    blurb:
      "A young developer with a distinct design language — Vento, Aveline, V1TER and LUM1NAR across Motor City, JVC and JVT.",
    seoTitle: "Object 1 Properties for Sale in Dubai | Hive Homes",
    seoDescription:
      "Browse Object 1 properties in Dubai — Vento Tower, Aveline Residences, V1TER, LUM1NAR and V1STARA in Motor City, JVC, JVT and Al Furjan. Hive Homes Dubai.",
    paragraphs: [
      "Object 1 is one of Dubai's fastest-growing new developers, backed by the TSZ Group. Its towers — Vento in Motor City, Aveline, V1TER and One Park Central in JVC, LUM1NAR in JVT and V1STARA in Al Furjan — combine contemporary architecture with well-planned amenity floors at mid-market prices.",
      "Object 1 offers investors an early-mover position with a developer that has delivered quickly and on time. We follow each launch and hold stock across its completed buildings.",
    ],
    cover: "/images/developers/object-1.jpg",
    coverAlt: "Jumeirah Lakes Towers reflected in the lake",
    keywords: [
      "object 1",
      "object1",
      "object one",
      "vento tower",
      "aveline",
      "sensia",
      "v1ter",
      "v1stara",
      "lum1nar",
      "evergr1n",
      "one park central",
    ],
    communitySlugs: ["jumeirah-village-circle", "dubai-land"],
    ctaLabel: "Speak to an Object 1 Specialist",
  },
];

/**
 * Homepage subset — the list above is ordered by prominence, and the
 * "Top Dubai Developers" carousel shows the head of it so a phone
 * doesn't lazy-load two dozen cover photos while it drifts.
 */
export const HOME_DEVELOPERS: DeveloperContent[] = DEVELOPERS.slice(0, 12);

export const DEVELOPERS_BY_SLUG: Record<string, DeveloperContent> =
  Object.fromEntries(DEVELOPERS.map((d) => [d.slug, d]));

export function findDeveloper(slug: string | undefined): DeveloperContent | undefined {
  if (!slug) return undefined;
  return DEVELOPERS_BY_SLUG[slug.toLowerCase()];
}

/**
 * Associates a live listing with a developer. The feed has no developer
 * field, so this matches the curated keyword needles against the
 * listing's location and title text (plus `developer` when a future
 * feed provides it).
 */
export function propertyMatchesDeveloper(
  property: {
    developer?: string;
    community?: string;
    subCommunity?: string;
    address?: string;
    title?: string;
  },
  developer: DeveloperContent,
): boolean {
  const haystack = [
    property.developer,
    property.community,
    property.subCommunity,
    property.address,
    property.title,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (!haystack) return false;
  return developer.keywords.some((needle) => haystack.includes(needle));
}
